import { Controller } from "@hotwired/stimulus";
import $ from "jquery";
import "slick-carousel";

const POST_VISIBILITY_THRESHOLD = 0.7;
const SCROLL_ROOT_SELECTOR = ".layout-responsive";

function resolveScrollRoot(element) {
  const candidate = element.closest(SCROLL_ROOT_SELECTOR);
  if (!candidate) return null;

  const overflowY = globalThis.getComputedStyle(candidate).overflowY;
  return overflowY === "auto" || overflowY === "scroll"
    ? candidate
    : null;
}

export default class FeedVideoController extends Controller {
  static targets = ["slider"];

  connect() {
    this.element.feedVideoController = this;

    this.scrollRoot = resolveScrollRoot(this.element);
    this.isPostVisible = false;

    this.handleTabVisibility = this.handleTabVisibility.bind(this);
    this.handleTurboBeforeCache = this.handleTurboBeforeCache.bind(this);
    this.handleTurboFrameLoad = this.handleTurboFrameLoad.bind(this);

    document.addEventListener(
      "visibilitychange",
      this.handleTabVisibility
    );

    document.addEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    );

    document.addEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad
    );

    this.setupPostObserver();
    this.observeSliderLayout();
    this.initializeSlider();
  }

  disconnect() {
    clearTimeout(this.readyRetryTimeout);
    this.layoutObserver?.disconnect();

    document.removeEventListener(
      "visibilitychange",
      this.handleTabVisibility
    );

    document.removeEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    );

    document.removeEventListener(
      "turbo:frame-load",
      this.handleTurboFrameLoad
    );

    this.destroySlider();

    this.postObserver?.disconnect();

    this.pauseAll();

    this.element.feedVideoController = null;
  }

  handleTurboBeforeCache() {
    clearTimeout(this.readyRetryTimeout);
    this.pauseAll();
  }

  initializeSlider() {
    if (!this.hasSliderTarget) return;

    const $slider = $(this.sliderTarget);

    if ($slider.hasClass("slick-initialized")) {
      // Turbo caches a cloned DOM tree, so Slick's generated markup survives
      // but its JavaScript instance does not. Keep the live slider intact
      // during navigation, then rebuild only a disconnected cached clone.
      if (this.sliderTarget.slick) return;

      this.resetCachedSliderMarkup($slider);
    }

    // Slick calculates slide widths from the rendered container. Turbo frames
    // and Bootstrap modals can connect while that container is still hidden,
    // which gives Slick a zero width and causes a visible second layout pass.
    if (this.sliderTarget.getBoundingClientRect().width === 0) {
      this.observeSliderLayout();
      return;
    }

    // A cached Turbo snapshot must not leave generated navigation behind.
    $slider.children(".slick-dots").remove();
    $slider.children(".post-media-arrow").remove();
    $(this.element).children(".post-media-dots").remove();

    $slider.on("init.feedVideo reInit.feedVideo", (event, slick) => {
      this.markSliderReady();
      this.layoutBreakpoint = slick.activeBreakpoint;
      this.syncActiveDot(slick);

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide();
        }
      });
    });

    $slider.on("beforeChange.feedVideo", (event, slick, current, next) => {
      // A breakpoint can clamp the track position without changing the chosen media.
      if (!this.selectingMedia && slick.activeBreakpoint === this.layoutBreakpoint) {
        this.selectedMediaIndex = next;
      }
      this.pauseAll();
    });

    $slider.on("afterChange.feedVideo", (event, slick) => {
      this.syncActiveDot(slick);
      if (this.restoreSelectionAfterChange) {
        this.restoreSelectionAfterChange = false;
        // Slick clears its animation lock after dispatching afterChange.
        requestAnimationFrame(() => this.goTo(this.selectedMediaIndex));
      }

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide();
        }
      });
    });

    $slider.on("breakpoint.feedVideo setPosition.feedVideo", (event, slick) => {
      this.syncActiveDot(slick);
      if (event.type === "breakpoint") {
        this.layoutBreakpoint = slick.activeBreakpoint;
        this.restoreSelectionAfterChange = slick.animating;
        if (!slick.animating) this.goTo(this.selectedMediaIndex);
      }

      requestAnimationFrame(() => {
        if (this.isPostVisible) {
          this.playCurrentSlide();
        }
      });
    });

    const isInModal = this.element.closest(".modal") !== null;

    const initialSlide = Number.parseInt(
      this.sliderTarget.dataset.initialSlide || "0",
      10
    );

    this.selectedMediaIndex = initialSlide;
    $(this.element).off("click.feedVideo").on("click.feedVideo", ".post-media-dots button", event => {
      this.goTo(Number(event.currentTarget.dataset.mediaIndex));
    });

    initializePostSlider(this.sliderTarget, initialSlide, !!isInModal);
  }

  observeSliderLayout() {
    if (!this.hasSliderTarget || this.layoutObserver) return;
    this.lastSliderWidth = null;

    this.layoutObserver = new globalThis.ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width || 0;
      if (width === this.lastSliderWidth) return;
      this.lastSliderWidth = width;
      cancelAnimationFrame(this.layoutFrame);
      if (width <= 0) return;

      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        if (this.element.isConnected) this.refresh();
      });
    });

    this.layoutObserver.observe(this.sliderTarget);
  }

  markSliderReady() {
    this.sliderTarget.classList.add("post-media-slider--ready");
    delete this.sliderTarget.dataset.initialSlide;
  }

  resetCachedSliderMarkup($slider) {
    const $track = $slider.children(".slick-list").children(".slick-track");

    const $slickSlides = $track.children(
      ".slick-slide:not(.slick-cloned)"
    );

    const currentIndex = Number.parseInt(
      $slickSlides.filter(".post-media-selected").attr("data-slick-index") || "0",
      10
    );

    // Slick's default row wrapper is:
    // slick-slide > row > original slide.
    // Reuse those original nodes so nested video/fancybox markup stays intact.
    let $originalSlides = $slickSlides.children().children();

    if ($originalSlides.length === 0) {
      $originalSlides = $slickSlides;
    }

    $originalSlides.detach().removeAttr("style");

    $slider.empty().append($originalSlides);

    $slider
      .removeClass(
        "slick-slider slick-initialized slick-dotted post-media-slider--ready"
      )
      .removeAttr("style");

    this.sliderTarget.dataset.initialSlide = currentIndex;
  }

  syncActiveDot(slick) {
    if (!slick || slick.unslicked) return;

    const index = Math.max(0, Math.min(
      this.selectedMediaIndex ?? slick.currentSlide, slick.slideCount - 1
    ));
    this.selectedMediaIndex = index;
    slick.$slides.removeClass("post-media-selected")
      .eq(index).addClass("post-media-selected");

    let $dots = $(this.element).children(".post-media-dots");
    if (slick.slideCount <= 1) {
      $dots.remove();
      return;
    }

    if ($dots.children().length !== slick.slideCount) {
      $dots.remove();
      $dots = $("<ul class=\"slick-dots post-media-dots\" aria-label=\"Post media\"></ul>");
      for (let i = 0; i < slick.slideCount; i += 1) {
        $("<li>").append($("<button>", {
          type: "button",
          "data-media-index": i,
          "aria-label": `Show media ${i + 1} of ${slick.slideCount}`,
          text: i + 1
        })).appendTo($dots);
      }
      $(this.element).append($dots);
    }

    $dots.children().removeClass("slick-active")
      .eq(index).addClass("slick-active");
    $dots.find("button").removeAttr("aria-current")
      .eq(index).attr("aria-current", "true");
  }

  // Browser fullscreen changes the viewport dimensions. Slick normally treats
  // that as a responsive breakpoint change and rebuilds the track, temporarily
  // disconnecting nested Stimulus controllers and disposing the fullscreen
  // Video.js player. Keep layout positioning enabled but defer the destructive
  // breakpoint refresh until fullscreen has ended.
  suspendSliderResponsiveUpdates() {
    if (this.resumeResponsiveUpdates || !this.hasSliderTarget) return;

    const $slider = $(this.sliderTarget);

    if (!$slider.hasClass("slick-initialized")) return;

    const slick = $slider.slick("getSlick");

    if (!slick || slick.unslicked) return;

    clearTimeout(slick.windowDelay);

    const checkResponsive = slick.checkResponsive;

    slick.checkResponsive = () => { };

    this.resumeResponsiveUpdates = () => {
      slick.checkResponsive = checkResponsive;
      slick.windowWidth = $(globalThis).width();
      this.resumeResponsiveUpdates = null;

      if (!slick.unslicked) {
        slick.checkResponsive(false, true);
      }
    };
  }

  resumeSliderResponsiveUpdates() {
    this.resumeResponsiveUpdates?.();
  }

  destroySlider() {
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = null;
    if (!this.hasSliderTarget) return;

    this.layoutObserver?.disconnect();
    this.layoutObserver = null;

    this.resumeSliderResponsiveUpdates();

    const $slider = $(this.sliderTarget);

    if ($slider.hasClass("slick-initialized")) {
      $slider.off(".feedVideo");
      $(this.element).off("click.feedVideo").children(".post-media-dots").remove();
      $slider.slick("unslick");
    }

    this.sliderTarget.classList.remove("post-media-slider--ready");
  }

  setupPostObserver() {
    this.postObserver = new IntersectionObserver(
      this.handlePostIntersection.bind(this),
      {
        root: this.scrollRoot,
        threshold: POST_VISIBILITY_THRESHOLD
      }
    );

    this.postObserver.observe(this.element);

    this.isPostVisible = this.isInViewportNow();

    if (this.isPostVisible) {
      requestAnimationFrame(() => this.playCurrentSlide());
    }
  }

  isInViewportNow() {
    const rect = this.element.getBoundingClientRect();

    if (rect.height === 0) return false;

    const rootRect = this.scrollRoot
      ? this.scrollRoot.getBoundingClientRect()
      : {
        top: 0,
        bottom: globalThis.innerHeight
      };

    const visibleHeight =
      Math.min(rect.bottom, rootRect.bottom) -
      Math.max(rect.top, rootRect.top);

    return (
      Math.max(0, visibleHeight) / rect.height >=
      POST_VISIBILITY_THRESHOLD
    );
  }

  handlePostIntersection(entries) {
    for (const entry of entries) {
      this.isPostVisible = entry.isIntersecting;

      if (entry.isIntersecting) {
        requestAnimationFrame(() => {
          this.playCurrentSlide();
        });
      } else {
        // IMPORTANT:
        // This is an automatic visibility pause.
        // Do NOT mark the video as userPaused.
        this.pauseAll();
      }
    }
  }

  handleVideoReady() {
    if (!this.isPostVisible) return;

    requestAnimationFrame(() => {
      this.playCurrentSlide();
    });
  }

  playCurrentSlide(retriesLeft = 10) {
    if (!this.isPostVisible) return;

    const slide = this.currentSlide();

    if (!slide) return;

    const wrapper = slide.querySelector(
      "[data-controller~='video-js']"
    );

    if (!wrapper) {
      // Current slide is an image.
      this.pauseAll();
      return;
    }

    if (!wrapper.videoController) {
      if (retriesLeft > 0) {
        clearTimeout(this.readyRetryTimeout);

        this.readyRetryTimeout = setTimeout(() => {
          this.playCurrentSlide(retriesLeft - 1);
        }, 150);
      }

      return;
    }

    // If the user manually paused this video, visibility changes must
    // never autoplay it again.
    if (wrapper.videoController.userPaused) {
      return;
    }

    // Pause every other video in this post.
    for (const video of this.videoWrappers()) {
      if (video !== wrapper) {
        video.videoController?.pause();
      }
    }

    if (!wrapper.videoController.isPlaying()) {
      setTimeout(() => {
        // Re-check everything because the user may have interacted with
        // the video while this timeout was waiting.
        if (!this.isPostVisible) return;

        if (wrapper.videoController?.userPaused) return;

        wrapper.videoController?.play();
      }, 100);
    }
  }

  pauseAll() {
    // IMPORTANT:
    // pause() here is an automatic/system pause.
    // VideoJsController will NOT set userPaused.
    for (const video of this.videoWrappers()) {
      video.videoController?.pause();
    }
  }

  videoWrappers() {
    return Array.from(
      this.element.querySelectorAll(
        "[data-controller~='video-js']"
      )
    );
  }

  handleTurboFrameLoad(event) {
    // Only react if this slider is inside the loaded frame.
    if (!event.target.contains(this.element)) return;

    requestAnimationFrame(() => this.refresh());
  }

  // ----------------------------------------------------
  // Slick Helpers
  // ----------------------------------------------------

  currentSlide() {
    if (!this.hasSliderTarget) return null;

    if (
      this.sliderTarget.classList.contains(
        "post-media-slider--single"
      )
    ) {
      return this.sliderTarget.querySelector(".post-media-item");
    }

    const $slider = $(this.sliderTarget);

    if (!$slider.hasClass("slick-initialized")) return null;

    const slick = $slider.slick("getSlick");

    if (!slick) return null;

    const slide = slick.$slides[this.selectedMediaIndex ?? slick.currentSlide];

    return slide || null;
  }

  currentIndex() {
    if (!this.hasSliderTarget) return 0;

    const $slider = $(this.sliderTarget);

    if (!$slider.hasClass("slick-initialized")) return 0;

    return this.selectedMediaIndex ?? $slider.slick("slickCurrentSlide");
  }

  slideCount() {
    if (!this.hasSliderTarget) return 0;

    const $slider = $(this.sliderTarget);

    if (!$slider.hasClass("slick-initialized")) return 0;

    return $slider.slick("getSlick").slideCount;
  }

  // ----------------------------------------------------
  // Navigation Helpers
  // ----------------------------------------------------

  next() {
    if (!this.hasSliderTarget) return;

    this.goTo(Math.min(this.currentIndex() + 1, this.slideCount() - 1));
  }

  prev() {
    if (!this.hasSliderTarget) return;

    this.goTo(Math.max(this.currentIndex() - 1, 0));
  }

  goTo(index) {
    if (!this.hasSliderTarget) return;

    const $slider = $(this.sliderTarget);
    if (!$slider.hasClass("slick-initialized")) return;
    const slick = $slider.slick("getSlick");
    if (slick.animating || !Number.isInteger(index)) return;

    this.pauseAll();
    this.selectedMediaIndex = Math.max(0, Math.min(index, slick.slideCount - 1));
    // Keep the last images selectable without leaving empty slots in the track.
    const position = Math.min(this.selectedMediaIndex,
      Math.max(0, slick.slideCount - slick.options.slidesToShow));
    this.selectingMedia = true;
    $slider.slick("slickGoTo", position);
    this.selectingMedia = false;
    this.syncActiveDot(slick);
    if (this.isPostVisible) this.playCurrentSlide();
  }

  // ----------------------------------------------------
  // Browser Visibility
  // ----------------------------------------------------

  handleTabVisibility() {
    if (document.hidden) {
      // Automatic pause. userPaused is NOT changed.
      this.pauseAll();
      return;
    }

    if (!this.isPostVisible) return;

    requestAnimationFrame(() => {
      this.playCurrentSlide();
    });
  }

  // ----------------------------------------------------
  // Public API
  // ----------------------------------------------------

  refresh() {
    if (!this.hasSliderTarget || !this.element.isConnected) return;
    if (this.sliderTarget.getBoundingClientRect().width <= 0) return;

    const $slider = $(this.sliderTarget);


    // Tab may have been hidden when this controller connected.
    // Initialize it now that it has a measurable width.
    if (
      !$slider.hasClass(
        "slick-initialized"
      )
    ) {
      this.initializeSlider();
      return;
    }


    // Important:
    // Don't hide/show the slider.
    // Just ask Slick to recalculate its layout.
    $slider.slick("setPosition");


    // Recalculate visibility because the post may have just
    // become visible after switching tabs.
    this.isPostVisible =
      this.isInViewportNow();


    if (this.isPostVisible) {
      requestAnimationFrame(() => {
        this.playCurrentSlide();
      });
    } else {
      this.pauseAll();
    }
  }

  // ----------------------------------------------------
  // Cleanup
  // ----------------------------------------------------

  teardown() {
    clearTimeout(this.readyRetryTimeout);
    clearTimeout(this.resizeTimeout);

    this.pauseAll();

    this.destroySlider();

    this.postObserver?.disconnect();
    this.postObserver = null;
  }
}

function initializePostSlider(slider, initialSlide = 0, isInModal = false) {
  if (!slider || slider.classList.contains("slick-initialized")) {
    return;
  }

  $(slider).slick({
    initialSlide: initialSlide,
    infinite: false,
    slidesToShow: isInModal ? 2 : 3,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: "<button type=\"button\" class=\"slick-prev post-media-arrow bg-dark border-2 border-solid border-fluorescent w-35px h-35px rounded-circle text-white z-3 cursor-pointer p-0 d-flex align-items-center justify-content-center translate-middle-y p-1\" aria-label=\"Previous post media\"><svg class=\"d-block pe-none flex-shrink-0\" aria-hidden=\"true\" viewBox=\"0 0 24 24\" fill=\"none\"><path d=\"m15 6-6 6 6 6\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg></button>",
    nextArrow: "<button type=\"button\" class=\"slick-next post-media-arrow bg-dark border-2 border-solid border-fluorescent w-35px h-35px rounded-circle text-white z-3 cursor-pointer p-0 d-flex align-items-center justify-content-center translate-middle-y p-1\" aria-label=\"Next post media\"><svg class=\"d-block pe-none flex-shrink-0\" aria-hidden=\"true\" viewBox=\"0 0 24 24\" fill=\"none\"><path d=\"m9 6 6 6-6 6\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg></button>",
    dots: false,
    draggable: true,
    swipe: true,
    touchMove: true,
    adaptiveHeight: false,

    responsive: [
      {
        breakpoint: 1700,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  });
}
